// The help pieces that draw: `HelpButton` (the small "?" that opens a topic),
// `HelpOverlay` (the dialog, from props only) and `HelpHost` (hears the
// buttons, looks the topic up in a `Help` registry and shows the overlay over
// the page; the plugin `shared-help` mounts the one the app uses).
import * as React from 'react';
import { createPortal } from 'react-dom';
import { HELP_OPEN_EVENT, openHelp, type Help, type HelpOpenDetail, type HelpTopic } from './index.ts';
import './help.css';

export interface HelpButtonProps {
  /** The topic's id, as registered. */
  topic: string;
  /** Read out and shown on hover; "How is this calculated?" by default. */
  label?: string;
}

/** Phosphor's Question, outlined. */
const QuestionIcon: React.FC = () => (
  <svg viewBox="0 0 256 256" aria-hidden="true" className="help-button-icon">
    <circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" strokeWidth="16" />
    <path d="M100 100a28 28 0 1 1 40 25c-8 4-12 10-12 19v6" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
    <circle cx="128" cy="184" r="12" fill="currentColor" />
  </svg>
);

/** The small "?" that opens a help topic. Stops the click there, so a clickable card behind it does not open too. */
export const HelpButton: React.FC<HelpButtonProps> = ({ topic, label = 'How is this calculated?' }) => (
  <button
    type="button"
    className="help-button"
    aria-label={label}
    title={label}
    aria-haspopup="dialog"
    data-track="help.open"
    data-track-value={topic}
    onClick={(e) => {
      e.stopPropagation();
      openHelp(topic, e.currentTarget);
    }}>
    <QuestionIcon />
  </button>
);

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface HelpOverlayProps {
  topic: HelpTopic;
  onClose: () => void;
}

/**
 * A modal dialog with the topic's title and body. The focus moves into it
 * and stays there (Tab and Shift+Tab cycle); Escape, the close button or a
 * click on the backdrop close it. The body scrolls when long.
 */
export const HelpOverlay: React.FC<HelpOverlayProps> = ({ topic, onClose }) => {
  const dialog = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  React.useEffect(() => {
    dialog.current?.querySelector<HTMLElement>('.help-close')?.focus();
  }, []);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialog.current) return;
    const items = [...dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  return (
    <div className="help-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} className="help-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
        <div className="help-head">
          <h2 id={titleId} className="help-title">
            {topic.title}
          </h2>
          <button type="button" className="help-close" aria-label="Close" data-track="help.close" onClick={onClose}>
            <svg viewBox="0 0 256 256" aria-hidden="true">
              <path d="M64 64l128 128M192 64L64 192" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="help-body" tabIndex={0}>
          {topic.body}
        </div>
      </div>
    </div>
  );
};

/**
 * Hears every `HelpButton`, shows the asked topic from `help` over the page,
 * and gives the focus back to the button when the overlay closes. A topic
 * nobody registered opens nothing.
 */
export const HelpHost: React.FC<{ help: Help }> = ({ help }) => {
  const [open, setOpen] = React.useState<HelpOpenDetail | undefined>();
  const [, setRevision] = React.useState(0);
  React.useEffect(() => help.subscribe(() => setRevision((r) => r + 1)), [help]);
  React.useEffect(() => {
    const onOpen = (e: Event) => setOpen((e as CustomEvent<HelpOpenDetail>).detail);
    window.addEventListener(HELP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(HELP_OPEN_EVENT, onOpen);
  }, []);
  const topic = open ? help.topic(open.id) : undefined;
  if (!open || !topic) return null;
  const close = () => {
    const opener = open.opener;
    setOpen(undefined);
    opener?.focus();
  };
  return createPortal(<HelpOverlay topic={topic} onClose={close} />, document.body);
};
